
import { apiService } from './apiService';
import { API_CONFIG } from '../config/apiConfig';
import { User, TipoUsuario, mapTipoUsuarioToRole, isTipoUsuarioGestor, stringOrNumberToTipoUsuario } from '../services/authService';
import { toast } from '@/hooks/use-toast';

// Function for basic encryption of localStorage data
const encryptLocalData = (data: string): string => {
  return btoa(data); // Base64 encoding - basic but better than plain text
};

// Interface for the login request object
interface LoginRequest {
  Nome: string;
  SenhaApp: string;
  UsuarioAPP: string;
}

// Interface representing the Vendedor da API
interface ApiVendedor {
  ID?: number;
  Nome?: string;
  Telefone?: string;
  SenhaApp?: string;
  VendedorID?: number;
  Gestor?: boolean;
  FormaDesconto?: number;
  TipoDesconto?: number;
  Avatar?: string;
  Email?: string;
  TipoUser?: number | string;
  TipoEmpresa?: string; // Adicionar campo para tipo de empresa
  // Outros campos que possam existir
}

// Interface for the CookieJar following the C# structure provided
interface CookieJar {
  SmartDueAdminToken: string;
  SmartDueUserEmail: string;
  SmartDueUserID: number;
  SmartDueUserName: string;
  SmartDueEmpresaID: number;
  SmartDueEmpresaNome: string;
  OneManagerLogoEmpresa: string;
  DataCriacao: string;
  CNPJ?: string; // Campo adicional que aparece no novo retorno
  TipoEmpresa?: string; // Campo para tipo de empresa
}

// Interface for the API login response
interface RetornoLoginAPI {
  CookieJar: CookieJar;
  PessoaVendedor: ApiVendedor;
  // Outros campos que possam existir
}

// Map API user to the application's user object
const mapApiUserToUser = (apiUser: ApiVendedor): Omit<User, 'password'> => {
  // Converter TipoUser da API para nosso enum interno (agora pode ser string ou number)
  const tipoUsuario = stringOrNumberToTipoUsuario(apiUser.TipoUser || 1);
  
  // Verificar se é gestor tanto pelo flag Gestor quanto pelo TipoUser
  const isGestor = apiUser.Gestor === true || isTipoUsuarioGestor(apiUser.TipoUser || 1);
  
  console.log(`Mapeando usuário: ${apiUser.Nome}, tipo: ${apiUser.TipoUser}, gestor: ${isGestor}`, apiUser);
  
  return {
    id: String(apiUser.ID || '0'),
    name: apiUser.Nome || '',
    email: apiUser.Email || '',
    username: apiUser.Email || apiUser.Nome || '',
    role: mapTipoUsuarioToRole(apiUser.TipoUser || 1),
    isManager: isGestor,
    tipoUsuario: tipoUsuario,
    avatar: apiUser.Avatar,
    vendedor_id: apiUser.VendedorID || apiUser.ID || 0,
    empresa_id: 0 // Será preenchido posteriormente com o valor do CookieJar
  };
};

// Save the API token in localStorage (now encrypted)
const saveApiToken = (token: string) => {
  if (token) {
    localStorage.setItem('api_auth_token', encryptLocalData(token));
    localStorage.setItem('HeaderToken', encryptLocalData(token));
    console.log('Token salvo com segurança');
  }
};

// Simplified function to attempt login
const attemptLogin = async (url: string, data: LoginRequest, useCorsProxy: boolean = false): Promise<RetornoLoginAPI | null> => {
  try {
    // If using CORS proxy
    const requestUrl = useCorsProxy 
      ? `https://corsproxy.io/?${encodeURIComponent(url)}`
      : url;
      
    console.log(`[Login] Tentativa em: ${requestUrl.split('?')[0]} ${useCorsProxy ? '(via proxy)' : '(diretamente)'}`);
    
    // Make the request
    const response = await fetch(requestUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(data),
      credentials: 'include' // Include cookies
    });
    
    console.log(`[Login] Status: ${response.status} ${response.statusText}`);
    
    // If the response is not successful
    if (!response.ok) {
      console.error(`[Login] Erro: ${response.status} ${response.statusText}`);
      const errorText = await response.text();
      console.error(`[Login] Detalhes do erro: ${errorText}`);
      return null;
    }
    
    // Try to process the response as JSON
    const responseText = await response.text();
    console.log('[Login] Resposta recebida:', responseText.substring(0, 100) + '...');
    
    try {
      const jsonData = JSON.parse(responseText);
      console.log(`[Login] Dados processados:`, jsonData);
      
      // Verificar se a resposta contém os dados esperados
      if (jsonData && jsonData.CookieJar && jsonData.CookieJar.SmartDueAdminToken) {
        return jsonData;
      } else {
        console.error('[Login] Resposta sem token ou malformada:', jsonData);
        return null;
      }
    } catch (error) {
      console.error('[Login] Erro ao analisar JSON:', error);
      console.error('[Login] Texto da resposta:', responseText);
      return null;
    }
  } catch (error) {
    console.error(`[Login] Erro na tentativa ${useCorsProxy ? 'com proxy' : 'diretamente'}:`, error);
    return null;
  }
};

// Authenticate user with API - Improved version for same domain
export const authenticateWithApi = async (username: string, password: string): Promise<User | null> => {
  try {
    // Verify if the API URL is configured
    const apiBaseUrl = localStorage.getItem('api_base_url');
    if (!apiBaseUrl) {
      console.error('URL da API não está configurada');
      return null;
    }

    // Create a normalized base URL (ensure it doesn't end with /)
    const baseUrl = apiBaseUrl.trim().endsWith('/') 
      ? apiBaseUrl.trim().slice(0, -1) 
      : apiBaseUrl.trim();
    
    // Check if we are on the same domain (facilitates CORS)
    const currentDomain = window.location.hostname;
    const apiDomain = new URL(baseUrl).hostname;
    const sameOrigin = currentDomain === apiDomain;
    
    console.log(`[Login] Domínio atual: ${currentDomain}, Domínio da API: ${apiDomain}, Mesmo domínio: ${sameOrigin}`);
    
    // Endpoint for login
    const loginUrl = `${baseUrl}/api/PessoaVendedors/LogarApp`;
    console.log('[Login] URL completa:', loginUrl);

    // Login data
    const loginData = {
      Nome: username,
      SenhaApp: password,
      UsuarioAPP: username
    };
    
    console.log('[Login] Dados:', { Nome: username, SenhaApp: '****', UsuarioAPP: username });
    
    // Try first with direct approach
    let result = await attemptLogin(loginUrl, loginData, false);
    
    // If it fails, try with CORS proxy
    if (!result) {
      console.log('[Login] Tentando com proxy CORS...');
      localStorage.setItem('use_cors_proxy', 'true');
      apiService.updateConfig(baseUrl, '', true);
      
      result = await attemptLogin(loginUrl, loginData, true);
      
      // If it worked with proxy, save this preference for future requests
      if (result) {
        localStorage.setItem('use_cors_proxy', 'true');
        console.log('[Login] Proxy CORS funcionou! Configuração salva para solicitações futuras.');
      }
    } else {
      // If it worked without proxy, ensure we're not using proxy unnecessarily
      localStorage.setItem('use_cors_proxy', 'false');
    }
    
    // Process result if successful
    if (result && result.CookieJar.SmartDueAdminToken) {
      // Save token - IMPORTANT: This is the token we need for API calls
      saveApiToken(result.CookieJar.SmartDueAdminToken);
      
      // Obter tipo de usuário da API (agora pode ser string ou number)
      const tipoUsuario = stringOrNumberToTipoUsuario(result.PessoaVendedor.TipoUser || 'Vendedor');
      
      // Verificar se é gestor pelo flag Gestor ou pelo tipo
      const isGestor = result.PessoaVendedor.Gestor === true || isTipoUsuarioGestor(result.PessoaVendedor.TipoUser || 'Vendedor');
      
      // Obter o vendedor_id e empresa_id diretamente do CookieJar conforme especificado
      const vendedorId = result.CookieJar.SmartDueUserID || 0;
      const empresaId = result.CookieJar.SmartDueEmpresaID || 0;
      
      console.log(`Usuário: ${result.CookieJar.SmartDueUserName}, tipo: ${result.PessoaVendedor.TipoUser}, gestor: ${isGestor}`);
      console.log(`ID do Vendedor (SmartDueUserID): ${vendedorId}, ID da Empresa (SmartDueEmpresaID): ${empresaId}`);
      
      const user: User = {
        id: String(result.CookieJar.SmartDueUserID || '0'),
        email: result.CookieJar.SmartDueUserEmail || username,
        username: result.CookieJar.SmartDueUserEmail || username,
        name: result.CookieJar.SmartDueUserName || result.PessoaVendedor?.Nome || username,
        role: mapTipoUsuarioToRole(result.PessoaVendedor.TipoUser || 'Vendedor'),
        password: password, // only for compatibility
        isManager: isGestor,
        tipoUsuario: tipoUsuario,
        avatar: result.PessoaVendedor?.Avatar,
        vendedor_id: vendedorId,
        empresa_id: empresaId
      };
      
      console.log(`Tipo de usuário definido como: ${user.role}, isManager: ${user.isManager}`);
      console.log(`vendedor_id = ${user.vendedor_id}, empresa_id = ${user.empresa_id}`);
      
      // Save company data
      if (result.CookieJar.SmartDueEmpresaID) {
        localStorage.setItem('empresa_id', String(result.CookieJar.SmartDueEmpresaID));
      }
      if (result.CookieJar.SmartDueEmpresaNome) {
        localStorage.setItem('empresa_nome', result.CookieJar.SmartDueEmpresaNome);
      }
      if (result.CookieJar.OneManagerLogoEmpresa) {
        localStorage.setItem('empresa_logo', result.CookieJar.OneManagerLogoEmpresa);
      }
      // Se existir CNPJ no retorno, salvar também
      if (result.CookieJar.CNPJ) {
        localStorage.setItem('empresa_cnpj', result.CookieJar.CNPJ);
      }
      
      // Use TipoEmpresa from API response, prioritizing PessoaVendedor.TipoEmpresa
      const tipoEmpresa = result.PessoaVendedor.TipoEmpresa || result.CookieJar.TipoEmpresa || 'Restaurante';
      localStorage.setItem('empresa_tipo', tipoEmpresa);
      console.log(`Tipo de Empresa definido como: ${tipoEmpresa}`);
      
      // Salvar usuário atual para auth local
      localStorage.setItem('currentUser', JSON.stringify(user));
      localStorage.setItem('isAuthenticated', 'true');
      
      console.log('[Login] Sucesso! Usuário:', user);
      
      toast({
        title: 'Login realizado com sucesso',
        description: `Bem-vindo, ${user.name}!`,
      });
      
      return user;
    }
    
    console.log('[Login] Falhou em todas as tentativas');
    
    toast({
      title: 'Erro de autenticação',
      description: 'Credenciais inválidas ou erro na API',
      variant: 'destructive',
    });
    
    return null;
  } catch (error) {
    console.error('[Login] Erro geral:', error);
    
    toast({
      title: 'Erro de conexão',
      description: error instanceof Error ? error.message : 'Erro ao conectar ao servidor',
      variant: 'destructive',
    });
    
    return null;
  }
};

// Fetch users from API
export const fetchUsersFromApi = async (): Promise<User[]> => {
  try {
    const endpoint = API_CONFIG.endpoints.users.list;
    const apiUsers = await apiService.get<ApiVendedor[]>(endpoint);
    
    // Map API users and add password (only for compatibility)
    const users: User[] = apiUsers.map(apiUser => {
      const tipoUsuario = (apiUser.TipoUser || 1) as TipoUsuario;
      return {
        ...mapApiUserToUser(apiUser),
        password: '1234', // Password default for compatibility (not used with API)
        tipoUsuario: tipoUsuario
      };
    }) as User[];
    
    return users;
  } catch (error) {
    console.error('Error fetching users from API:', error);
    toast({
      title: 'User synchronization error',
      description: 'Failed to fetch users from ERP',
      variant: 'destructive',
    });
    return [];
  }
};

// Function to synchronize users from API with local storage
export const syncUsersWithApi = async (): Promise<boolean> => {
  try {
    // Fetch users from API
    const apiUsers = await fetchUsersFromApi();
    
    if (apiUsers.length === 0) {
      return false;
    }
    
    // Save in local storage (localStorage)
    localStorage.setItem('restaurant_users', JSON.stringify(apiUsers));
    
    toast({
      title: 'Users synchronized',
      description: `${apiUsers.length} users synchronized successfully`,
    });
    
    return true;
  } catch (error) {
    console.error('Error synchronizing users:', error);
    return false;
  }
};
